/**
 * Follow-up prompt extraction, shared by web and native.
 *
 * Deterministic flows already emit their suggested next questions as a
 * trailing `Try asking: A · B` line in the final markdown part. Clients
 * previously rendered that as prose, so the user had to retype the question
 * they were being offered (RAI-1095).
 *
 * Parsing the existing line — rather than adding a new event payload kind —
 * keeps this a pure client change: no protocol version, no capability gate,
 * and older clients keep showing the same readable sentence they always did.
 * The backend remains free to move to structured prompts later; this parser
 * is the only place that would change.
 */

/** Marker the deterministic flows emit before their suggestions. */
const FOLLOW_UP_MARKER = "Try asking:";

/** Flows join prompts with a middle dot; tolerate the ASCII fallbacks too. */
const PROMPT_SEPARATOR = /\s+[·•|]\s+/;

const MAX_PROMPTS = 6;
const MAX_PROMPT_LENGTH = 140;

export type T_FollowUpPromptExtraction = {
  /** Prompts to render as tappable pills. Empty when there are none. */
  prompts: string[];
  /** The markdown with the follow-up line removed, ready to render as prose. */
  markdown: string;
};

/**
 * Splits a trailing "Try asking:" line out of assistant markdown.
 *
 * Only a line that *starts* with the marker is treated as the follow-up list,
 * so prose that happens to mention the phrase mid-sentence is left alone.
 */
export function extractFollowUpPrompts(
  markdown: string,
): T_FollowUpPromptExtraction {
  const lines = markdown.split("\n");
  const markerIndex = lines.findIndex((line) =>
    line.trimStart().startsWith(FOLLOW_UP_MARKER),
  );
  if (markerIndex === -1) return { markdown, prompts: [] };

  const markerLine = lines[markerIndex] ?? "";
  const rawPrompts = markerLine
    .trimStart()
    .slice(FOLLOW_UP_MARKER.length)
    .split(PROMPT_SEPARATOR)
    .map((prompt) => prompt.trim().replace(/^[-–—]\s*/, ""))
    .filter(
      (prompt) => prompt.length > 0 && prompt.length <= MAX_PROMPT_LENGTH,
    );

  // Nothing usable after the marker: leave the markdown untouched rather than
  // silently deleting a line the user would otherwise have read.
  if (rawPrompts.length === 0) return { markdown, prompts: [] };

  const seen = new Set<string>();
  const prompts: string[] = [];
  for (const prompt of rawPrompts) {
    const key = prompt.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    prompts.push(prompt);
    if (prompts.length >= MAX_PROMPTS) break;
  }

  const remaining = [
    ...lines.slice(0, markerIndex),
    ...lines.slice(markerIndex + 1),
  ]
    .join("\n")
    .replace(/\n{3,}$/, "\n")
    .trimEnd();

  return { markdown: remaining, prompts };
}
